import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { HardDrive, Cloud, Loader2, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const ALL = "__all__";
const fmtBytes = (b) => {
  const n = Number(b) || 0;
  if (n < 1024) return `${n} B`;
  const u = ["KB", "MB", "GB", "TB"]; let v = n / 1024, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${u[i]}`;
};

function UsageCard({ usage, refreshing, onRefresh }) {
  const [openPer, setOpenPer] = useState(false);
  return (
    <div className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="admin-storage-usage">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h3 className="font-head font-semibold text-foreground flex items-center gap-2"><HardDrive className="h-4 w-4 text-amber-400" /> Spazio occupato dagli allegati</h3>
        <Button onClick={onRefresh} disabled={refreshing} data-testid="admin-usage-refresh-btn" variant="outline" className="h-8 shrink-0">
          {refreshing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Aggiorna
        </Button>
      </div>
      <p className="text-sm text-muted-foreground mb-5">Riepilogo dello spazio usato dai file delle pratiche, suddiviso per destinazione di archiviazione.</p>
      <div className="rounded-lg border border-border bg-muted/40 p-4" data-testid="admin-usage-emergent">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-foreground font-medium"><HardDrive className="h-4 w-4 text-amber-400" /> Storage Emergent</span>
          <span className="font-mono text-amber-500" data-testid="admin-usage-emergent-bytes">{fmtBytes(usage.emergent_bytes)}</span>
        </div>
        <div className="mt-3 h-2 w-full rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-amber-500 rounded-full" style={{ width: `${Math.min(100, (usage.emergent_bytes / (usage.emergent_limit_bytes || 1)) * 100).toFixed(1)}%` }} />
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground"><span>{usage.emergent_count} file</span><span>Limite {fmtBytes(usage.emergent_limit_bytes)}</span></div>
      </div>
      <div className="rounded-lg border border-border bg-muted/40 p-4 mt-4" data-testid="admin-usage-gcs">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-foreground font-medium"><Cloud className="h-4 w-4 text-cyan-400" /> Google Cloud Storage</span>
          <span className="font-mono text-cyan-500">{fmtBytes(usage.gcs_bytes)}</span>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">{usage.gcs_count} file{usage.gcs_count === 0 ? " · nessun file su Google Cloud" : ""}</div>
      </div>
      <div className="rounded-lg border border-border bg-muted/40 p-4 mt-4" data-testid="admin-usage-wasabi">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-foreground font-medium"><Cloud className="h-4 w-4 text-green-500" /> Wasabi</span>
          <span className="font-mono text-green-500">{fmtBytes(usage.wasabi_bytes || 0)}</span>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">{usage.wasabi_count || 0} file{(usage.wasabi_count || 0) === 0 ? " · nessun file su Wasabi" : ""}</div>
      </div>
      <div className="rounded-lg border border-border bg-muted/40 p-4 mt-4" data-testid="admin-usage-b2">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-foreground font-medium"><Cloud className="h-4 w-4 text-rose-500" /> Backblaze B2</span>
          <span className="font-mono text-rose-500" data-testid="admin-usage-b2-bytes">{fmtBytes(usage.b2_bytes || 0)}</span>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">{usage.b2_count || 0} file{(usage.b2_count || 0) === 0 ? " · nessun file su Backblaze B2" : ""}</div>
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-border pt-4 text-sm">
        <span className="text-foreground font-medium">Totale occupato</span>
        <span className="font-mono text-foreground font-semibold" data-testid="admin-usage-total-bytes">{fmtBytes(usage.total_bytes)}</span>
      </div>
      {Array.isArray(usage.per_pratica) && usage.per_pratica.length > 0 && (
        <div className="mt-6" data-testid="admin-usage-per-pratica">
          <button type="button" onClick={() => setOpenPer((o) => !o)} data-testid="admin-usage-per-pratica-toggle" className="flex items-center gap-2 text-sm font-head font-semibold text-foreground mb-3 w-full text-left">
            {openPer ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />} Pratiche che occupano più spazio
          </button>
          {openPer && (
            <div className="space-y-2.5 max-h-[400px] overflow-y-auto pr-1">
              {usage.per_pratica.map((p) => (
                <div key={p.pratica_id} className="rounded-md border border-border bg-muted/30 px-3 py-2" data-testid={`admin-usage-pratica-${p.pratica_id}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="truncate pr-3"><span className="font-mono text-amber-500">{p.numero_pratica}</span>{p.nome_assistito ? <span className="text-muted-foreground"> · {p.nome_assistito}</span> : null}</span>
                    <span className="font-mono shrink-0">{fmtBytes(p.bytes)}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-cyan-500/70 rounded-full" style={{ width: `${Math.max(3, (p.bytes / (usage.per_pratica[0].bytes || 1)) * 100).toFixed(1)}%` }} />
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">{p.count} file</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ThumbsCard({ status, busy, onRebuild, allMode }) {
  return (
    <div className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="admin-thumbs-config">
      <h3 className="font-head font-semibold text-foreground flex items-center gap-2"><HardDrive className="h-4 w-4 text-amber-400" /> Miniature allegati</h3>
      <p className="text-sm text-muted-foreground mt-1 mb-4">Le anteprime di foto e PDF vengono generate automaticamente al caricamento. Se alcune non compaiono, rigenerale qui{allMode ? " (gli studi vengono elaborati uno dopo l'altro)" : ""}.</p>
      {status && (
        <div className="rounded-lg border border-border bg-muted/40 p-4" data-testid="admin-thumbs-status">
          <div className="flex items-center justify-between text-sm"><span>Allegati con anteprima</span><span className="font-mono text-amber-500" data-testid="admin-thumbs-count">{status.with_thumb}/{status.total}</span></div>
          <div className="mt-3 h-2 w-full rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${status.total ? Math.min(100, (status.with_thumb / status.total) * 100).toFixed(1) : 0}%` }} />
          </div>
          <div className="mt-2 text-xs text-muted-foreground">{status.missing > 0 ? `${status.missing} da generare` : "Tutte le anteprime sono generate"}</div>
        </div>
      )}
      <Button onClick={onRebuild} disabled={busy || status?.running} data-testid="admin-thumbs-rebuild-btn" className="mt-4 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
        {(busy || status?.running) ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Rigenerazione in corso…</> : <>Rigenera miniature</>}
      </Button>
    </div>
  );
}

// Spazio occupato + miniature per tutti gli studi o per lo studio selezionato (superadmin).
export default function AdminStoragePanels() {
  const [tenants, setTenants] = useState([]);
  const [tid, setTid] = useState(ALL);
  const [usage, setUsage] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [thumbs, setThumbs] = useState(null);
  const [busy, setBusy] = useState(false);
  const q = tid === ALL ? "" : `?tenant_id=${tid}`;
  const loadThumbs = async () => { try { const { data } = await api.get(`/admin/storage/thumbs/status${q}`); setThumbs(data); return data; } catch { return null; } };
  const loadUsage = async (notify) => {
    setRefreshing(true);
    try { const { data } = await api.get(`/admin/storage/usage${q}`); setUsage(data); if (notify) toast.success("Spazio ricalcolato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };
  useEffect(() => { api.get("/admin/tenants").then(({ data }) => setTenants(data || [])).catch(() => {}); }, []);
  useEffect(() => { setUsage(null); setThumbs(null); loadUsage(false); loadThumbs(); }, [tid]); // eslint-disable-line react-hooks/exhaustive-deps
  const rebuild = async () => {
    setBusy(true);
    try {
      await api.post("/admin/storage/thumbs/rebuild", { tenant_id: tid === ALL ? "" : tid });
      toast.success("Rigenerazione miniature avviata");
      const poll = async () => {
        const d = await loadThumbs();
        if (d && d.running) setTimeout(poll, 2000);
        else { setBusy(false); if (d) toast.success(`Miniature aggiornate (${d.with_thumb}/${d.total})`); }
      };
      setTimeout(poll, 1500);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <div data-testid="admin-storage-panels">
      <div className="max-w-2xl mt-8 flex items-center gap-3" data-testid="admin-storage-scope">
        <span className="text-sm text-muted-foreground">Dati di:</span>
        <Select value={tid} onValueChange={setTid}>
          <SelectTrigger className="w-72 h-9" data-testid="admin-storage-tenant"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tutti gli studi</SelectItem>
            {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.studio_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {usage ? <UsageCard usage={usage} refreshing={refreshing} onRefresh={() => loadUsage(true)} /> : <div className="max-w-2xl p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>}
      <ThumbsCard status={thumbs} busy={busy} onRebuild={rebuild} allMode={tid === ALL} />
    </div>
  );
}
